/*
 * What a session says while it is being loaded out of a file, and afterwards.
 *
 * Four strings and two composers, in their own file for the reason
 * `session/world.ts` is one: they are read in `main.ts`'s boot sequence and
 * asserted in `dev/selfcheck/session.ts`, and a sentence that can only be
 * reached by pressing *load* on a title screen is a sentence nothing checks.
 *
 * **Every line here is interface text, in the sans hand.** The world says
 * nothing about a load — a file is a thing the browser holds, not a thing that
 * happened in the simulation — so this is one of the few surfaces the client is
 * allowed to write sentences for at all (plan §3 invariant 7). What it must not
 * do is compose a sentence about the *world*: the year, the town and the figure
 * come off the simulation like everything else, and the refusals come off the
 * boundary, word for word (`crates/sim-wasm`'s `RestoreError`).
 *
 * # The one thing these lines are for
 *
 * A replay restores the **world**, not the session (decision D-5). The player is
 * host-side — position, journal, the ground they walked, the people they met
 * (Phase 3 decision 10) — so a loaded file puts the world back exactly where it
 * was and puts the player back approximately where they stood, with an empty
 * journal and an unwalked map. That is a surprise worth one sentence, and
 * {@link RESTORE_KEPT} is it.
 */

/** What the page says while it is rebuilding the recorded world. */
const RESTORING_TITLE = "loading the saved session";

/**
 * The second line of it, which counts.
 *
 * A restore is the recorded world rebuilt and then every recorded tick re-run
 * (`world/wasm.ts`), so there are two waits inside one: the bootstrap, which
 * cannot report progress because it happens inside one call to the simulation,
 * and the re-run, which can. The count is the honest half, and it is why the
 * first line says *loading* rather than a number.
 */
export function restoringLine(done: number, total: number): string {
  if (total === 0) {
    // A file recorded before its session ran a tick: the world is the bootstrap
    // and there is nothing to count, which the line says rather than reading
    // "0 of 0 ticks" while the page waits on construction.
    return `${RESTORING_TITLE}\n\nrebuilding the world it was recorded in`;
  }

  return `${RESTORING_TITLE}\n\nreplaying ${done.toLocaleString()} of ${total.toLocaleString()} recorded ticks`;
}

/**
 * The one line a restored session says about what did not come back.
 *
 * Said **after** the load rather than before it, because before it there is
 * nothing to compare against: a player who has just watched a world rebuild and
 * finds their journal empty is the person this sentence is for. It names the
 * three things by what they are rather than by their panels, and it does not
 * apologise — what a replay holds is what the simulation holds, and the rest was
 * never in the file (`crates/sim-wasm`'s `restored_position`).
 */
export const RESTORE_KEPT =
  "the world and where you stood came back · your journal, the ground you walked and the people" +
  " you met are not in a replay";

/** What a restored session says it did, with the sentence above under it. */
export function restoredLine(ticks: number): string {
  return `loaded ${ticks.toLocaleString()} recorded tick${ticks === 1 ? "" : "s"} — ${RESTORE_KEPT}`;
}

/**
 * What the title screen says over a file it could not use.
 *
 * The lead is the client's, because the client is what asked for a file; the
 * sentence after it is the **simulation's own**, word for word — a version this
 * build does not read, a document that is not a replay, a material table that is
 * not this build's, or the tick a re-run stopped matching the recording at. None
 * of those is a thing this file could say better, and re-writing them here would
 * be a second opinion about a format that lives in Rust.
 */
export function refusedLine(refusal: string): string {
  return `that file is not a session this build can play — ${refusal}`;
}

/** And what it says when the browser could not read the file at all. */
export const UNREADABLE_FILE = "that file could not be read";
